// ─────────────────────────────────────────────────────────────────────────
// quickCreateNotice — config + resolution logic for the "you just created a
// brand-new record" popup shown after an in-dropdown quick-create.
//
// WHY this lives in its own module rather than inside QuickCreateEditField:
// anything that imports AddFormV1/EditFormV1 (which QuickCreateEditField does,
// lazily, and which the admin screen does statically) cannot be rendered under
// vitest — react-pdf's DocumentViewer needs DOMMatrix and jsdom has none. That
// is a pre-existing trap in this repo. Keeping the *decisions* (is the notice
// on? what is the record called? who created it?) in a dependency-free module
// means the part that can actually be wrong is unit-testable, and the React
// file is left with nothing but rendering.
//
// Everything here is generic: no module name, no field name, no collection is
// special-cased. A field opts in through the admin config key
// `quickCreateNotice`.
// ─────────────────────────────────────────────────────────────────────────

import { fetchJsonWithAuth, getStoredUser } from '../../services/authApi';
import { AUTH_URL } from '../../services/apiConfig';
import { fetchUserName, userDisplayName } from '../detail/userNames';

/**
 * The product-owner-approved default. It is the DEFAULT rather than something
 * the admin must type, so that a field which merely switches the notice on
 * still shows the correct warning. Admin config overrides it verbatim.
 */
export const DEFAULT_QUICK_CREATE_NOTICE_MESSAGE =
  'A new client has been created successfully. Please contact your Organization Admin to '
  + 'configure the required forms, workflows, permissions, and client-specific details before '
  + 'proceeding with further operations.';

export const DEFAULT_QUICK_CREATE_NOTICE_TITLE = 'New record created';
export const DEFAULT_QUICK_CREATE_RECORD_LABEL = 'Record name';
export const DEFAULT_QUICK_CREATE_CREATOR_LABEL = 'Created by';

const str = (v) => (v === undefined || v === null ? '' : String(v).trim());

/**
 * normalizeQuickCreateNotice — field config → the resolved notice settings, or
 * null when this field has not opted in.
 *
 * `enabled` defaults to TRUE when the key is present at all: an admin who took
 * the trouble to author a `quickCreateNotice` object meant to switch it on, and
 * a config object that silently does nothing is the worst of the two failure
 * modes. Only an explicit `enabled: false` turns it off.
 */
export function normalizeQuickCreateNotice(field) {
  const raw = field?.quickCreateNotice;
  if (!raw || typeof raw !== 'object') return null;
  if (raw.enabled === false) return null;

  return {
    enabled: true,
    title: str(raw.title) || DEFAULT_QUICK_CREATE_NOTICE_TITLE,
    message: str(raw.message) || DEFAULT_QUICK_CREATE_NOTICE_MESSAGE,
    // Both traceability lines are ON unless explicitly disabled — same reasoning
    // as `enabled` above.
    showCreator: raw.showCreator !== false,
    showRecordName: raw.showRecordName !== false,
    recordNameField: str(raw.recordNameField),
    recordLabel: str(raw.recordLabel) || DEFAULT_QUICK_CREATE_RECORD_LABEL,
    creatorLabel: str(raw.creatorLabel) || DEFAULT_QUICK_CREATE_CREATOR_LABEL,
  };
}

// Keys that commonly hold a record's human name, tried in order when the admin
// configured no recordNameField and the field carries no lookup displayField.
const NAME_LIKE_KEYS = [
  'clientName', 'companyName', 'name', 'title', 'fullName',
  'displayName', 'label', 'employerName', 'vendorName',
];

const readPath = (record, key) => {
  if (!record || !key) return undefined;
  return String(key).split('.').reduce((acc, part) => (
    acc && typeof acc === 'object' ? acc[part] : undefined
  ), record);
};

const scalarName = (v) => {
  if (typeof v === 'string' || typeof v === 'number') return str(v);
  // Labeled-select / reference shapes ({label,value}) show up on records too.
  if (v && typeof v === 'object') return str(v.label ?? v.name ?? v.title ?? '');
  return '';
};

/**
 * pickRecordName — the record's display name, by the configured key first, then
 * the field's lookup displayField, then well-known name-like keys, then ANY
 * string key whose name ends in "name". Purely best-effort: an empty result is
 * a legitimate outcome and must not stop the notice from appearing.
 */
export function pickRecordName(record, { recordNameField = '', displayField = '' } = {}) {
  if (!record || typeof record !== 'object') return '';
  for (const key of [recordNameField, displayField, ...NAME_LIKE_KEYS]) {
    if (!key) continue;
    const value = scalarName(readPath(record, key));
    if (value) return value;
  }
  const loose = Object.keys(record).find((k) => (
    /name$/i.test(k) && typeof record[k] === 'string' && record[k].trim()
  ));
  return loose ? str(record[loose]) : '';
}

/**
 * pickCreatorId — the id of whoever the stored record credits with its
 * creation, across the several shapes the gateway's audit stamp can take
 * (plain id, ObjectId wrapper, nested user object).
 */
export function pickCreatorId(record) {
  if (!record || typeof record !== 'object') return '';
  const candidates = [
    record.createdBy, record.created_by, record.createdById,
    record.createdUserId, record.recordMeta?.createdBy, record.ownerId,
  ];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null || candidate === '') continue;
    if (typeof candidate === 'object') {
      const nested = candidate.$oid ?? candidate.userId ?? candidate._id ?? candidate.id;
      const value = str(nested);
      if (value) return value;
      continue;
    }
    const value = str(candidate);
    if (value) return value;
  }
  return '';
}

/** currentUserName — the signed-in user's display name, '' when unknowable. */
export function currentUserName(getUser = getStoredUser) {
  try {
    return userDisplayName(getUser()) || '';
  } catch {
    return '';
  }
}

const defaultFetchRecord = (module, recordId) => fetchJsonWithAuth(
  AUTH_URL,
  `/module/list?module=${encodeURIComponent(module)}&id=${encodeURIComponent(recordId)}`,
).then((res) => {
  const rows = res?.data?.data ?? res?.data ?? [];
  return Array.isArray(rows) ? rows[0] : rows;
});

/**
 * resolveQuickCreateNoticeDetails — the two traceability lookups.
 *
 * FAIL OPEN, deliberately and on every branch: the warning message is the point
 * of this popup, the name and the creator are garnish. A directory that is down,
 * a record the list endpoint cannot return, a module name that does not resolve
 * — none of it may suppress the notice or throw into the form. Every await is
 * individually caught and degrades to an empty string.
 *
 * The creator falls back to the signed-in user because in this flow the creator
 * IS the current user: they pressed "Add More" seconds ago. That fallback is
 * therefore accurate, not a guess.
 *
 * Dependencies are injected (with real defaults) so this is testable without a
 * network or a browser.
 */
export async function resolveQuickCreateNoticeDetails({
  module,
  recordId,
  notice,
  displayField = '',
  deps = {},
} = {}) {
  const {
    fetchRecord = defaultFetchRecord,
    fetchUserNameFn = fetchUserName,
    getUser = getStoredUser,
  } = deps;

  let record = null;
  if (module && recordId) {
    try {
      record = await fetchRecord(module, recordId);
    } catch {
      record = null; // fail open
    }
  }

  const recordName = notice?.showRecordName === false
    ? ''
    : pickRecordName(record, { recordNameField: notice?.recordNameField, displayField });

  let creatorName = '';
  if (notice?.showCreator !== false) {
    const creatorId = pickCreatorId(record);
    if (creatorId) {
      try {
        creatorName = str(await fetchUserNameFn(creatorId));
      } catch {
        creatorName = ''; // fail open
      }
    }
    if (!creatorName) creatorName = currentUserName(getUser);
  }

  return { recordName, creatorName };
}
